import { useStore } from "../store/globalState";
import {
  AuraLayout,
  CashIcon,
  CircularProgress,
  ElevatedCircle,
  KapitalIcon,
  Layout,
  PageTitle,
  SuccessIcon,
} from "lola-framework-ui-test";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  APP_ROUTES,
  flavorClassName,
  isFlavorB,
} from "../constants";
import { amountFormatter, getCurrentDateFormatted } from "../utils";
import type { ITransaction } from "../interfaces/transactions";
import { TransactionsStatus } from "../utils/commons";
import { useSessionStore } from "../store/useSessionStore";
import {
  createOperation,
  getQuote,
  getAllPaymentMethods,
} from "../services/api";
import type {
  IQuoteRequest,
  IQuoteResponse,
  ICreateOperationRequest,
} from "../services/api";
import type { AxiosError } from "axios";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { safeCapture } from "../utils/posthog";
import {
  resetFirstSendWarning,
  wasFirstSendWarningShown,
} from "../hooks/useFirstSendWarning";
import { useLocation } from "react-router-dom";

/**
 * Check if the error is a session expired error (LINK_NOT_FOUND)
 * These errors are handled globally by useSessionExpiredHandler
 */
const isSessionExpiredError = (err: unknown): boolean => {
  const axiosError = err as AxiosError<{ error?: { code?: string } }>;
  const errorCode = axiosError?.response?.data?.error?.code;
  return errorCode === "LINK_NOT_FOUND" || axiosError?.response?.status === 404;
};

/**
 * Check if the error is a quote expired error (QUOTE_EXPIRED)
 * This happens when the user takes too long in CIP and the signed JWT quote expires
 */
const isQuoteExpiredError = (err: unknown): boolean => {
  const axiosError = err as AxiosError<{ error?: { code?: string } }>;
  return axiosError?.response?.data?.error?.code === "QUOTE_EXPIRED";
};

/**
 * Module-level flag to prevent duplicate operation creation.
 * Unlike useRef, this survives component unmount/remount cycles
 * (StrictMode, routing re-mounts, etc.) so only ONE executeOperation
 * can ever be in-flight at a time. Reset when the operation finishes
 * (success or error) so future navigations to this page work normally.
 */
let isOperationInFlight = false;

export const SendingMoneyPage = memo(() => {
  const { t } = useTranslation();
  const {
    theme,
    setTransactions,
    quoteState,
    setQuoteState,
    paymentState,
    setSentMoneyToast,
  } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const { dropzoneColors, title } = colorConfig;
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Module-level guard: prevents duplicate execution from StrictMode
    // double-effects, routing re-mounts, or any other scenario that
    // causes the effect to fire more than once.
    if (isOperationInFlight) {
      console.log("[SendingMoneyPage] Skipping - operation already in flight");
      return;
    }
    isOperationInFlight = true;

    // Snapshot quoteState from the store at effect start so the async
    // flow always uses the same values regardless of re-renders.
    const capturedQuoteState = useStore.getState().quoteState;

    // Track when the operation starts to ensure minimum display time
    const operationStartTime = Date.now();

    /**
     * Request a fresh quote using the same parameters as the expired one.
     * Uses data from capturedQuoteState and session config.
     */
    const refreshQuote = async (): Promise<IQuoteResponse> => {
      const sourceCountry = session.data?.config?.sourceCountry || "US";
      const destinationCountry =
        session.data?.config?.destinationCountry || "MX";
      const request: IQuoteRequest = {
        sourceCountry,
        destinationCountry,
        fundingMethod: capturedQuoteState!.fundingMethod,
        payoutMethod: capturedQuoteState!.payoutMethod,
        amount: capturedQuoteState!.sendAmount,
        currency: capturedQuoteState!.sourceCurrency,
      };
      if (capturedQuoteState!.cardId) {
        request.cardId = capturedQuoteState!.cardId;
      }
      console.log("[SendingMoneyPage] Requesting fresh quote:", request);
      return await getQuote(request);
    };

    /**
     * Handle a successful operation response: create local transaction and navigate.
     * Ensures minimum 2 seconds of display time before showing success state.
     */
    const handleOperationSuccess = (
      operation: Awaited<ReturnType<typeof createOperation>>
    ) => {
      const {
        transactions: txList,
        operations,
        setOperations,
        setPhonePromptSkipped,
      } = useStore.getState();

      // Operation created: ask for the phone again on the next one if the
      // user tapped "Ahora no" this time.
      setPhonePromptSkipped(false);
      // The user is no longer on their first send.
      resetFirstSendWarning();

      const newTransaction: ITransaction = {
        operationId: operation.operationId,
        status:
          operation.status === "pending"
            ? TransactionsStatus.PENDING
            : TransactionsStatus.COMPLETED,
        amount: amountFormatter(operation.receiveAmount),
        currency: operation.destinationCurrency,
        date: getCurrentDateFormatted(),
      };

      setTransactions([...txList, newTransaction]);

      const opIdx = operations.findIndex(
        (o) => o.operationId === operation.operationId
      );
      if (opIdx >= 0) {
        const next = [...operations];
        next[opIdx] = operation;
        setOperations(next);
      } else {
        setOperations([operation, ...operations]);
      }

      // Calculate elapsed time and ensure minimum 2 seconds of display
      const elapsedTime = Date.now() - operationStartTime;
      const minimumDisplayTime = 2000;
      const remainingTime = Math.max(0, minimumDisplayTime - elapsedTime);

      setTimeout(() => {
        setIsSuccess(true);
        setTimeout(() => {
          redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, {
            replace: true,
            state: { from: location.pathname },
          });
        }, 3000);
      }, remainingTime);
    };

    const executeOperation = async () => {
      const linkId = session.data?.linkId;
      const signedQuote = capturedQuoteState?.signedQuote;

      let fundingPaymentMethodId: string | undefined;

      // Resolve funding payment method from stored payment methods
      if (linkId) {
        try {
          const paymentMethods = await getAllPaymentMethods(linkId);
          const fundingMethod = capturedQuoteState?.fundingMethod;
          if (fundingMethod === "card") {
            const card =
              paymentMethods.cards.find(
                (c) => c.paymentMethodId === paymentState.payment?.cardId
              ) ||
              paymentMethods.cards.find((c) => c.isDefault && c.enabled) ||
              paymentMethods.cards.find((c) => c.enabled);
            fundingPaymentMethodId = card?.paymentMethodId;
          } else if (fundingMethod === "bank_account") {
            const ba =
              paymentMethods.bankAccounts.find(
                (b) => b.isDefault && b.enabled
              ) || paymentMethods.bankAccounts.find((b) => b.enabled);
            fundingPaymentMethodId = ba?.paymentMethodId;
          }
        } catch (pmErr) {
          console.error(
            "[SendingMoneyPage] Failed to fetch payment methods:",
            pmErr
          );
        }
      }

      // Fallback: try the selected card from the store's paymentState
      if (!fundingPaymentMethodId) {
        const { paymentState } = useStore.getState();
        if (paymentState.payment?.cardId) {
          fundingPaymentMethodId = paymentState.payment.cardId;
        }
      }

      try {
        // DEBUG: Log quote info to diagnose QUOTE_EXPIRED issues
        if (capturedQuoteState) {
          const expiresAt = new Date(capturedQuoteState.expiresAt).getTime();
          const now = Date.now();
          const remainingMs = expiresAt - now;
          console.log("[SendingMoneyPage] Quote debug:", {
            quoteId: capturedQuoteState.quoteId,
            expiresAt: capturedQuoteState.expiresAt,
            remainingSeconds: Math.floor(remainingMs / 1000),
            isExpired: remainingMs <= 0,
            clientTime: new Date().toISOString(),
            signedQuotePreview: signedQuote?.slice(0, 50) + "...",
          });
        } else {
          console.log("[SendingMoneyPage] WARNING: quoteState is null!");
        }

        if (!linkId) {
          throw new Error("No linkId found in session");
        }
        if (!signedQuote) {
          throw new Error("No signed quote available");
        }
        if (!fundingPaymentMethodId) {
          throw new Error("No funding payment method selected");
        }

        const operationRequest: ICreateOperationRequest = {
          signedQuote,
          fundingPaymentMethodId,
        };

        const operation = await createOperation(linkId, operationRequest);
        safeCapture("ux.send.processing.operation.created", {
          success: true,
          operation_id: operation.operationId,
          status: operation.status,
          send_amount: operation.sendAmount,
          receive_amount: operation.receiveAmount,
          source_currency: operation.sourceCurrency,
          destination_currency: operation.destinationCurrency,
          total_cost: operation.totalCost,
          funding_payment_method_id: operation.fundingPaymentMethodId,
          first_send_warning_shown: wasFirstSendWarningShown(),
        });
        handleOperationSuccess(operation);
      } catch (err) {
        // If quote expired, silently refresh and retry once
        if (isQuoteExpiredError(err) && capturedQuoteState) {
          console.log(
            "[SendingMoneyPage] Quote expired, silently refreshing and retrying..."
          );
          try {
            const newQuote = await refreshQuote();

            console.log("[SendingMoneyPage] Fresh quote obtained:", {
              quoteId: newQuote.quoteId,
              expiresAt: newQuote.expiresAt,
            });

            const retryLinkId = session.data?.linkId;
            if (!retryLinkId) {
              throw new Error("No linkId found in session");
            }

            const operation = await createOperation(retryLinkId, {
              signedQuote: newQuote.signedQuote,
              fundingPaymentMethodId: fundingPaymentMethodId!,
            });

            // Update quoteState AFTER the operation succeeds so the store
            // update doesn't trigger a re-render/re-mount mid-retry
            setQuoteState(newQuote);
            handleOperationSuccess(operation);
            return;
          } catch (retryErr) {
            console.error(
              "[SendingMoneyPage] Retry after quote refresh also failed:",
              retryErr
            );
            setIsSuccess(false);
            setSentMoneyToast({
              visible: true,
              type: "error",
              message: t("landing.transaction.error"),
            });
            // Fall through to normal error handling with the retry error
            // eslint-disable-next-line no-ex-assign
            err = retryErr;
          }
        }

        // Log full error details for debugging
        const axiosError = err as AxiosError<{
          success?: boolean;
          error?: { code?: string; message?: string };
        }>;
        const serverError = axiosError?.response?.data?.error;

        console.error("[SendingMoneyPage] Failed to create operation:", {
          message: axiosError?.message,
          status: axiosError?.response?.status,
          serverErrorCode: serverError?.code,
          serverErrorMessage: serverError?.message,
          fullResponse: axiosError?.response?.data,
        });

        // If session expired (LINK_NOT_FOUND), the global handler already navigates to error page
        // Don't override it with a generic error message
        if (isSessionExpiredError(err)) {
          return;
        }

        safeCapture("ux.send.processing.operation.created", {
          success: false,
          error: serverError?.message ?? axiosError?.message,
          error_code: serverError?.code ?? "OPERATION_CREATE_FAILED",
          first_send_warning_shown: wasFirstSendWarningShown(),
        });

        // Repeated-send protection (backend). These are not outages: send the
        // user back to where they can act instead of the "service unavailable" page.
        if (serverError?.code === "SEND_COOLDOWN") {
          const retryAfterSeconds = Number(
            (serverError as { details?: { retryAfterSeconds?: number } })
              .details?.retryAfterSeconds
          );
          useStore
            .getState()
            .setRetryAvailableAt(
              Date.now() +
                (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
                  ? retryAfterSeconds
                  : 60) *
                  1000
            );
          redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
          return;
        }

        if (serverError?.code === "OPERATION_IN_PROGRESS") {
          setSentMoneyToast({
            visible: true,
            type: "error",
            message: t("landing.transaction.inProgress"),
          });
          redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
          return;
        }

        redirectTo(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      } finally {
        // Always release the lock so the user can retry from the error page
        // or create a new operation on a future visit.
        isOperationInFlight = false;
      }
    };

    executeOperation();

    // No cleanup function — we WANT the operation to run to completion
    // even if React unmounts/remounts this component (StrictMode, routing).
    // The module-level isOperationInFlight flag prevents duplicates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use quoteState.sendAmount for display — it IS persisted to localStorage
  // (amountState is NOT persisted and resets to "" after CIP redirect, causing NaN)
  const displayAmount = quoteState?.sendAmount ?? 0;

  const FinalIcon = isSuccess ? (
    <CircularProgress variant="full" size={140}
      strokeWidth={6} colors={dropzoneColors}>
      <ElevatedCircle
        iconBackground="transparent"
        shadowVariant="inset"
      >
        <SuccessIcon size={50} colors={dropzoneColors} />
      </ElevatedCircle>
    </CircularProgress>
  ) : (
    <CircularProgress size={140}
      strokeWidth={6} colors={dropzoneColors}>
      <ElevatedCircle
        iconBackground="transparent"
        shadowVariant="inset"
      >
        <CashIcon size={50} colors={dropzoneColors} />
      </ElevatedCircle>
    </CircularProgress>
  );

  const contentAlignClassName = isFlavorB ? "content-center" : "";
  const content = isFlavorB ? (
    <section className="sending-money-container">
      <KapitalIcon
        size={120}
        colors={dropzoneColors}
        className="animate-spin bg-transparent"
      />
      <section className="sending-caption">
        <PageTitle
          highlight={t("transfer.transferring")}
          highlightColor={title}
          secudnary={`$${amountFormatter(displayAmount)}`}
          secudnaryColor={title}
        />
      </section>
    </section>
  ) : (
    <section className="sending-money-container">
      {FinalIcon}
      <section className="sending-caption landing-container">
        <h1 className="foreground highlight text-centered">
          {t("transfer.your.deposit", {
            transfer: `$${amountFormatter(displayAmount)}`,
          })}
          <span className="span-block lola-title-h1 h1">
            {t("transfer.deposit.of", {
              transfer: `$${amountFormatter(displayAmount)}`,
            })}
          </span>
        </h1>

        <p className="foreground text-centered subtitle">
          {t("transfer.is.in.progress")}
        </p>
      </section>
    </section>
  );

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`${flavorClassName}`}
    >
      <Layout.Content
        className={`loading-section sending-money-section ${contentAlignClassName}`}
      >
        {content}
      </Layout.Content>
    </AuraLayout>
  );
});
